import {Observable} from "rxjs";
import {QuoteViewModels} from "../../../quote/models/QuoteViewModel";
import {
    isOpen,
    isTmcOrder,
    OrderRecord,
    OrderRecords,
    OrderSide,
    TmcLeg,
    TmcOrderRecord
} from "../../models/OrderRecord";
import {QuoteRequest, QuoteRequests, TmcQuoteRequestLeg} from "../../../quote/models/QuoteRequest";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {OrderStore} from "../order/OrderStore";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {flatMap} from "../../../js/util/UtilFunctions";
import {isIressOrder} from "../../models/iress/IressOrderRecord";

const QuoteBufferSeconds = 1;
const QuoteBufferTime = QuoteBufferSeconds * 1000;

export class OrderQuoteSourceBuilder {

    public static readonly id = "orderQuoteSourceBuilder";

    /** @ngInject */
    public constructor(private readonly orderStore: OrderStore,
                       private readonly quoteStore: QuoteStore) {
    }

    /*
     * The way this works is a little bit magic, but from testing it seems to be fine.
     * It has two subscriptions to the quote source, the first will simply emit all the quotes as they happen
     * while the second acts as the buffer emitter. This buffer emitter will wait for an emission from the quote
     * source, only responding to the first one in a "QuoteBufferTime" period that it receives and ignoring the rest,
     * it will then delay for "QuoteBufferTime" before finally emitting a signal, which will then prompt the "buffer"
     * observable to emit its most recent value.
     * It is done this way instead of a simple bufferTime as the assumption is that we will be getting all our
     * price updates coming through in a small window of time instead of a steady stream, thus rather than
     * simply splitting the updates down by an arbitrary time window, we instead want to wait for a value
     * emission to happen, wait for an appropriate amount of time so we can be "confident" the updates have all
     * arrived, and then emit the updated quote list after that.
     * From testing, this method does not seem to either skip or duplicate any records no matter if the value passed to
     * throttleTime and delay are the same value or different values, though keeping them the same value seems to make
     * more sense at this time
     */
    public build(): Observable<QuoteViewModels> {
        const quotes$ = this.orderStore
                            .orders
                            .map((orders) => this.buildRequests(orders))
                            .switchMap((orders) => this.quoteStore.scan(orders))
                            .publishReplay(1)
                            .refCount();

        const buffer$ = quotes$.throttleTime(QuoteBufferTime).delay(QuoteBufferTime);

        return quotes$.buffer(buffer$)
                      .filter((buffer) => buffer.length > 0)
                      .map((buffer) => buffer[buffer.length - 1])
                      .filter((quotes) => quotes.length > 0)
                      .startWith([]);
    }

    private buildRequests(orders: OrderRecords): QuoteRequests {
        return orders.filter((o) => isOpen(o))
                     .map((o) => {
                         if (isTmcOrder(o)) {
                             return this.buildTmcOrderRequests(o);
                         } else {
                             return [this.buildSingleOrderRequest(o)];
                         }
                     }).reduce(flatMap, []);
    }

    /* This code isn't fully correct right now, but will work given the current structures we have */
    private buildSingleOrderRequest(order: OrderRecord): QuoteRequest {
        if (isIressOrder(order)) {
            return {
                code: order.code,
                exchange: order.exchange,
                portfolioType: PortfolioType.BROKER
            };
        } else {
            return {
                code: order.code,
                exchange: order.exchange,
                portfolioType: PortfolioType.SIMULATED
            };
        }
    }

    private buildTmcOrderRequests(order: TmcOrderRecord): QuoteRequests {
        const legs = order.legs.map((l) => this.buildRequestLeg(l));
        const portfolioType = isIressOrder(order) ? PortfolioType.BROKER : PortfolioType.SIMULATED;

        const tmcQuote =  {
            code: order.code,
            exchange: order.exchange,
            portfolioType,
            opposite: order.side === OrderSide.MULTI_OPPOSITE,
            legs
        };
        const legQuotes = order.legs.map((l) => ({code: l.code, exchange: l.exchange, portfolioType}));
        return [tmcQuote, ...legQuotes];
    }

    private buildRequestLeg(leg: TmcLeg): TmcQuoteRequestLeg {
        const ratioMultiplier = (leg.side === OrderSide.BUY) ? 1 : -1;
        const ratio = leg.ratio * ratioMultiplier;
        return {
            code: leg.code,
            exchange: leg.exchange,
            ratio
        };
    }
}
